/**
 * Which company a composer is writing about, and whether it has a record that
 * employer could claim — so a template's `{{enlace_ficha}}` can become the link.
 *
 * An employer on the outreach side is an `employer_key`, not a company row
 * (README, "Empresas que contratan"): a scraped offer has no company until
 * somebody publishes it. So the answer is found the way the rest of the panel
 * finds it (`employer_identity.ts`, 0044):
 *
 *  1. a record the composer was opened on (a company's cover, its Correo tab);
 *  2. the prospect row's `company_id`, which 0044 links when it can;
 *  3. `companies.employer_key` — but only when exactly one row carries it.
 *     Three rows fold to `lab-xxi`; a claim link to the wrong one of three is a
 *     stranger's record in somebody's inbox, so two candidates is no answer.
 *
 * Then it asks whether anybody manages that record already. Every "no link"
 * comes back with the sentence that says why, for the composer to show next to
 * the placeholder it leaves in place.
 */

import { claimUrl, isClaimable } from "../company_claim";

export interface ClaimLinkTarget {
    companyId?: string | null;
    employerKey?: string | null;
}

export type ClaimLinkAnswer = { url: string; why: null } | { url: null; why: string };

interface CompanyRow {
    id: unknown;
    slug?: string | null;
    is_claimed?: boolean | null;
}

function idOf(value: unknown): string | null {
    if (!value) return null;
    if (typeof value === "object") {
        const id = (value as { id?: unknown }).id;
        return id ? String(id) : null;
    }
    return String(value);
}

export async function resolveClaimLink(data: any, target: ClaimLinkTarget): Promise<ClaimLinkAnswer> {
    if (!data) return { url: null, why: "No se pudo consultar la ficha de la empresa." };

    let companyId = (target.companyId ?? "").trim() || null;
    const key = (target.employerKey ?? "").trim() || null;

    try {
        if (!companyId && key) {
            const prospects = await data.collection("company-prospects")
                .find({ where: { employer_key: ["==", key] }, limit: 1 });
            companyId = idOf(((prospects?.data ?? []) as { company_id?: unknown }[])[0]?.company_id);
        }
        if (!companyId && key) {
            const rows = await data.collection("companies")
                .find({ where: { employer_key: ["==", key] }, limit: 3 });
            const found = (rows?.data ?? []) as CompanyRow[];
            if (found.length > 1) {
                return {
                    url: null,
                    why: "Hay más de una ficha con el nombre de esta empresa. Escribe desde la ficha correcta y el enlace se rellena solo."
                };
            }
            companyId = idOf(found[0]?.id);
        }
        if (!companyId) {
            return { url: null, why: "No tenemos ficha de esta empresa, así que no hay enlace para reclamarla." };
        }

        const [companyPage, memberPage] = await Promise.all([
            data.collection("companies").find({ where: { id: ["==", companyId] }, limit: 1 }),
            data.collection("company-members").find({ where: { company_id: ["==", companyId] }, limit: 1 })
        ]);
        const company = ((companyPage?.data ?? []) as CompanyRow[])[0];
        if (!company) {
            return { url: null, why: "No tenemos ficha de esta empresa, así que no hay enlace para reclamarla." };
        }
        const hasMembers = ((memberPage?.data ?? []) as unknown[]).length > 0;
        if (!isClaimable(company, hasMembers)) {
            return { url: null, why: "Esta empresa ya tiene cuenta en SustenTalent: no hay ficha que reclamar." };
        }
        return { url: claimUrl({ id: String(company.id), slug: company.slug ?? null }), why: null };
    } catch {
        return { url: null, why: "No se pudo consultar la ficha de la empresa. Vuelve a elegir la plantilla." };
    }
}
